import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/index';
import { logout } from '../../store/slices/authSlice';
import { getStatusCounts } from '../../store/slices/poRequestSlice';
import { USER_ROLES, type UserRole } from '../../constants';

interface SidebarProps {
  currentScreen: string;
  isOpen: boolean;
}

interface NavigationItem {
  id: string;
  label: string;
  icon: string;
  userRole: UserRole[];
}

interface NavigationGroup {
  id: string;
  label: string;
  icon: string;
  items: NavigationItem[];
  isOpen: boolean;
  userRole: UserRole[];
}

const Sidebar: React.FC<SidebarProps> = ({ currentScreen, isOpen }) => {
  const { isDarkMode, toggleTheme } = useTheme();
  const { expandedGroups, toggleGroup, sidebarWidth, setSidebarWidth, isResizing, setIsResizing, isMinimized, isMobile, toggleSidebar, toggleMinimize } = useSidebar();
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state: RootState) => state.auth);
  const { statusCounts } = useSelector((state: RootState) => state.poRequest);
  const dispatch = useDispatch<AppDispatch>();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(0);

  // Determine if sidebar should show icon-only mode based on width or minimized state
  // On mobile, always show full mode when sidebar is open
  const isIconOnlyMode = isMobile ? false : (sidebarWidth < 150 || isMinimized);

  // Helper function to check if user has required role
  const hasRole = useCallback((requiredRoles: UserRole[], userRole?: string): boolean => {
    if (!userRole) return false;
    return requiredRoles.includes(userRole as UserRole);
  }, []);

  // Get all navigation groups (for search functionality)
  const getAllNavigationGroups = useCallback(() => {
    if (!user?.role) return [];

    const navigationGroups: NavigationGroup[] = [
      {
        id: 'talep-yonetimi',
        label: 'Talep Yönetimi',
        icon: 'fas fa-clipboard-list',
        userRole: [USER_ROLES.ADMIN, USER_ROLES.USER, USER_ROLES.PURCHASER],
        isOpen: expandedGroups['talep-yonetimi'] || false,
        items: [
          { id: 'create-request', label: 'Talep Oluştur', icon: 'fas fa-plus-circle', userRole: [USER_ROLES.ADMIN, USER_ROLES.USER, USER_ROLES.PURCHASER] },
          { id: 'request-evaluation', label: 'Talep Değerlendirme', icon: 'fas fa-list', userRole: [USER_ROLES.ADMIN, USER_ROLES.PURCHASER] }
        ]
      },
      {
        id: 'teklif-yonetimi',
        label: 'Teklif Yönetimi',
        icon: 'fas fa-file-contract',
        isOpen: expandedGroups['teklif-yonetimi'] || false,
        userRole: [USER_ROLES.ADMIN, USER_ROLES.PURCHASER],
        items: [
          { id: 'draft-quote', label: 'Teklif Talebi Hazırlama', icon: 'fas fa-paper-plane', userRole: [USER_ROLES.ADMIN, USER_ROLES.USER, USER_ROLES.PURCHASER] },
          { id: 'quote-tracking', label: 'Teklif Mail Yönetimi', icon: 'fas fa-envelope', userRole: [USER_ROLES.ADMIN, USER_ROLES.PURCHASER] },
          { id: 'quote-evaluation', label: 'Teklif Değerlendirme', icon: 'fas fa-star', userRole: [USER_ROLES.ADMIN, USER_ROLES.PURCHASER] }
        ]
      },
      /*{
        id: 'siparis-yonetimi',
        label: 'Sipariş Yönetimi',
        icon: 'fas fa-shopping-cart',
        isOpen: expandedGroups['siparis-yonetimi'] || false,
        userRole: [USER_ROLES.ADMIN, USER_ROLES.PURCHASER],
        items: [
          { id: 'negotiation-quote', label: 'Müzakere Teklifi Hazırlama', icon: 'fas fa-handshake', userRole: [USER_ROLES.ADMIN, USER_ROLES.PURCHASER] },
          { id: 'purchase-order', label: 'Satın Alma Siparişi Hazırlama', icon: 'fas fa-shopping-cart', userRole: [USER_ROLES.ADMIN, USER_ROLES.PURCHASER] }
        ]
      },*/
        {
        id: 'rapor-surecleri',
        label: 'Raporlar',
        icon: 'fas fa-chart-line',
        isOpen: expandedGroups['rapor-surecleri'] || false,
        userRole: [USER_ROLES.ADMIN, USER_ROLES.USER, USER_ROLES.PURCHASER],
        items: [
          { id: 'request-info', label: 'Talep Durumu', icon: 'fas fa-history', userRole: [USER_ROLES.ADMIN, USER_ROLES.USER, USER_ROLES.PURCHASER] },
          { id: 'request-tracking', label: 'Talep Takibi', icon: 'fas fa-clipboard-list', userRole: [USER_ROLES.ADMIN, USER_ROLES.USER, USER_ROLES.PURCHASER] },
          { id: 'product-inquiry', label: 'Ürün Sorgulama', icon: 'fas fa-search', userRole: [USER_ROLES.ADMIN, USER_ROLES.USER, USER_ROLES.PURCHASER] },
          { id: 'item-quote-report', label: 'Ürün Bazlı Teklif Raporu', icon: 'fas fa-chart-bar', userRole: [USER_ROLES.ADMIN, USER_ROLES.USER, USER_ROLES.PURCHASER] },
          { id: 'order-invoice-comparison', label: 'Sipariş-Fatura Karşılaştırma', icon: 'fas fa-balance-scale', userRole: [USER_ROLES.ADMIN, USER_ROLES.PURCHASER] }
        ]
      }
    ];

    return navigationGroups
      .filter(group => hasRole(group.userRole, user.role))
      .map(group => ({
        ...group,
        items: group.items.filter(item => hasRole(item.userRole, user.role))
      }))
      .filter(group => group.items.length > 0); // Only show groups that have visible items
  }, [user?.role, hasRole, expandedGroups]);

  // Filter navigation groups and items based on user role
  const filteredNavigationGroups = useMemo(() => {
    return getAllNavigationGroups();
  }, [getAllNavigationGroups]);

  // Get all searchable items (flattened from all groups)
  const allSearchableItems = useMemo(() => {
    return filteredNavigationGroups.flatMap(group => 
      group.items.map(item => ({
        ...item,
        groupLabel: group.label,
        groupIcon: group.icon
      }))
    );
  }, [filteredNavigationGroups]);

  // Filter items based on search query
  const filteredSearchItems = useMemo(() => {
    if (!searchQuery.trim()) return allSearchableItems;
    
    const query = searchQuery.toLowerCase().trim();
    return allSearchableItems.filter(item => 
      item.label.toLowerCase().includes(query) ||
      item.groupLabel.toLowerCase().includes(query)
    );
  }, [allSearchableItems, searchQuery]);

  // Handle search mode toggle
  const handleSearchToggle = useCallback(() => {
    setIsSearchMode(prev => {
      if (!prev) {
        // Opening search mode
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 100);
      } else {
        // Closing search mode
        setSearchQuery('');
      }
      return !prev;
    });
  }, []);

  // Handle escape key to close search
  useEffect(() => {
    if (!isSearchMode) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsSearchMode(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isSearchMode]);

  useEffect(() => {
    if (user?.id) {
      dispatch(getStatusCounts({
        CreatedBy: user.id,
        ApproverUserId: user.id,
        DecisionUserId: user.id,
        PurchaserId: user.id
      }));
    }
  }, [dispatch, user?.id, location.pathname]);

  // Resize logic
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    startXRef.current = e.clientX;
    startWidthRef.current = sidebarWidth;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - startXRef.current;
      const newWidth = Math.max(60, Math.min(500, startWidthRef.current + deltaX)); // Min 60px, Max 500px
      setSidebarWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [sidebarWidth, setSidebarWidth, setIsResizing]);

  const getNavItemClasses = (itemId: string) => {
    const isActive = currentScreen === itemId;
    return `flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium cursor-pointer transition-all duration-200 ${isActive
        ? 'text-white bg-blue-600 shadow-lg'
        : 'text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-gray-700'
      }`;
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isMobile && isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={toggleSidebar}
        />
      )}

      <div
        ref={sidebarRef}
        className={`
          h-screen bg-white dark:bg-gray-900
          ${isResizing ? '' : 'transition-all duration-500 ease-in-out'}
          ${isOpen ? 'opacity-100' : 'w-0 opacity-0'}
          overflow-hidden
          relative
          ${isResizing ? 'select-none' : ''}
          flex flex-col
          ${isMobile ? 'fixed top-0 left-0 z-50 shadow-2xl' : ''}
        `}
        style={{
          width: isOpen ? (isMobile ? '280px' : (isMinimized ? '60px' : `${sidebarWidth}px`)) : '0px'
        }}
      >
        {/* Sidebar Toggle Button - Desktop Only */}
        {!isMobile && isOpen && (
          <div className="flex items-center justify-between p-3 flex-shrink-0">
            <button
              onClick={toggleMinimize}
              className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle sidebar minimize"
            >
              <i className="fas fa-bars text-lg"></i>
            </button>
            {!isIconOnlyMode && (
              <button
                onClick={handleSearchToggle}
                className={`p-2 rounded-lg transition-colors ${
                  isSearchMode 
                    ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900' 
                    : 'text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
                aria-label="Search"
              >
                <i className="fas fa-search text-lg"></i>
              </button>
            )}
          </div>
        )}

        {/* Search Input - Show when search mode is active */}
        {isSearchMode && !isIconOnlyMode && (
          <div className="p-3 flex-shrink-0 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Ekran ara..."
                className="w-full px-3 py-2 pl-10 pr-10 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500"></i>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className={`flex-1 px-2 space-y-0.5 overflow-y-auto overflow-x-visible scrollbar-hide ${isIconOnlyMode ? 'px-1' : ''}`}>
          {isSearchMode && !isIconOnlyMode ? (
            // Search Results View
            <div className="space-y-1 pt-2">
              {filteredSearchItems.length > 0 ? (
                filteredSearchItems.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => {
                      navigate(`/${item.id}`);
                      setIsSearchMode(false);
                      setSearchQuery('');
                    }}
                    className={getNavItemClasses(item.id)}
                  >
                    <i className={`${item.icon} text-sm w-4 text-center`}></i>
                    <div className="flex-1 flex flex-col">
                      <span className="flex items-center gap-1.5 min-w-0">
                        <span className="truncate">{item.label}</span>
                        {item.id === 'create-request' && (statusCounts?.data?.draftCount ?? 0) > 0 && (
                          <span className="flex-shrink-0 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-semibold bg-blue-500 text-white dark:bg-blue-600">
                            {statusCounts.data.draftCount}
                          </span>
                        )}
                        {item.id === 'request-evaluation' && (statusCounts?.data?.pendingCount ?? 0) > 0 && (
                          <span className="flex-shrink-0 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-semibold bg-blue-500 text-white dark:bg-blue-600">
                            {statusCounts.data.pendingCount}
                          </span>
                        )}
                        {item.id === 'draft-quote' && (statusCounts?.data?.approvedCount ?? 0) > 0 && (
                          <span className="flex-shrink-0 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-semibold bg-blue-500 text-white dark:bg-blue-600">
                            {statusCounts.data.approvedCount}
                          </span>
                        )}
                        {item.id === 'quote-evaluation' && (statusCounts?.data?.readyToEvaluateCount ?? 0) > 0 && (
                          <span className="flex-shrink-0 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-semibold bg-blue-500 text-white dark:bg-blue-600">
                            {statusCounts.data.readyToEvaluateCount}
                          </span>
                        )}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 opacity-75">
                        {item.groupLabel}
                      </span>
                    </div>
                    {currentScreen === item.id && (
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <i className="fas fa-search text-2xl mb-2 opacity-50"></i>
                  <p className="text-sm">Sonuç bulunamadı</p>
                </div>
              )}
            </div>
          ) : !isIconOnlyMode ? (
            // Full expanded view - show groups with text
            filteredNavigationGroups.map((group) => (
              <div key={group.id} className={`space-y-0.5 ${group.id === 'talep-yonetimi' ? 'pt-2 -mx-2 px-2' : 'pt-2'}`}>
                {/* Group Header */}
                <div
                  onClick={() => toggleGroup(group.id)}
                  className="flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-semibold cursor-pointer transition-all duration-300 ease-in-out transform hover:scale-[1.02] text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-gray-700"
                >
                  <i className={`${group.icon} text-sm w-4 text-center`}></i>
                  <span className="flex-1">{group.label}</span>
                  <i className={`fas fa-chevron-down text-xs transition-transform duration-300 ease-in-out ${expandedGroups[group.id] ? 'rotate-180' : 'rotate-0'
                    }`}></i>
                </div>

                {/* Group Items */}
                <div
                  className={`ml-3 space-y-0.5 border-l-2 border-gray-200 dark:border-gray-700 pl-3 overflow-hidden transition-all duration-300 ease-in-out ${expandedGroups[group.id]
                      ? 'max-h-96 opacity-100'
                      : 'max-h-0 opacity-0'
                    }`}
                >
                  <div className="space-y-0.5">
                    {group.items.map((item, index) => (
                      <div key={`wrapper-${item.id}`}>
                        <div
                          key={item.id}
                          onClick={() => {
                            navigate(`/${item.id}`);
                          }}
                          className={`${getNavItemClasses(item.id)} transition-all duration-300 ease-in-out ${expandedGroups[group.id]
                              ? 'opacity-100 translate-x-0'
                              : 'opacity-0 -translate-x-2'
                            }`}
                          style={{
                            transitionDelay: expandedGroups[group.id] ? `${index * 50}ms` : '0ms'
                          }}
                        >
                          <i className={`${item.icon} text-sm w-4 text-center`}></i>
                          <span className="flex-1 flex items-center gap-1.5 min-w-0">
                            <span className="truncate">{item.label}</span>
                            {item.id === 'create-request' && (statusCounts?.data?.draftCount ?? 0) > 0 && (
                              <span className="flex-shrink-0 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-semibold bg-blue-500 text-white dark:bg-blue-600">
                                {statusCounts.data.draftCount}
                              </span>
                            )}
                            {item.id === 'request-evaluation' && (statusCounts?.data?.pendingCount ?? 0) > 0 && (
                              <span className="flex-shrink-0 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-semibold bg-blue-500 text-white dark:bg-blue-600">
                                {statusCounts.data.pendingCount}
                              </span>
                            )}
                            {item.id === 'draft-quote' && (statusCounts?.data?.approvedCount ?? 0) > 0 && (
                              <span className="flex-shrink-0 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-semibold bg-blue-500 text-white dark:bg-blue-600">
                                {statusCounts.data.approvedCount}
                              </span>
                            )}
                            {item.id === 'quote-evaluation' && (statusCounts?.data?.readyToEvaluateCount ?? 0) > 0 && (
                              <span className="flex-shrink-0 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-semibold bg-blue-500 text-white dark:bg-blue-600">
                                {statusCounts.data.readyToEvaluateCount}
                              </span>
                            )}
                          </span>
                          {currentScreen === item.id && (
                            <div className="w-2 h-2 bg-white rounded-full"></div>
                          )}
                        </div>
                        {item.id === 'draft-quote' && (
                          <div className={`ml-7 mt-1 mb-1 text-xs text-blue-600 dark:text-blue-400 opacity-75 transition-all duration-300 ease-in-out ${expandedGroups[group.id]
                              ? 'opacity-100 translate-x-0'
                              : 'opacity-0 -translate-x-2'
                            }`}
                            style={{
                              transitionDelay: expandedGroups[group.id] ? `${(index + 1) * 50}ms` : '0ms'
                            }}>
                            <i className="fas fa-info-circle mr-1"></i>
                            Satıcıdan teklif isteği oluşturma
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))
          ) : (
            // Icon-only mode (collapsed or narrow width) - show all screen icons
            <div className="space-y-1 pt-2 -mx-2 px-2 overflow-visible">
              {filteredNavigationGroups.flatMap(group => group.items).map((item) => (
                <div
                  key={item.id}
                  onClick={() => {
                    navigate(`/${item.id}`);
                  }}
                  className={`flex items-center justify-center px-2 py-2.5 rounded-lg text-sm font-medium cursor-pointer transition-all duration-200 relative overflow-visible ${currentScreen === item.id
                      ? 'text-white bg-blue-600 shadow-lg'
                      : 'text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-gray-700'
                    }`}
                  title={item.label}
                >
                  <i className={`${item.icon} text-lg`}></i>
                  {item.id === 'create-request' && (statusCounts?.data?.draftCount ?? 0) > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[1rem] h-4 px-1 rounded-full text-[10px] font-semibold bg-blue-500 text-white dark:bg-blue-600 flex items-center justify-center">
                      {statusCounts.data.draftCount}
                    </span>
                  )}
                  {item.id === 'request-evaluation' && (statusCounts?.data?.pendingCount ?? 0) > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[1rem] h-4 px-1 rounded-full text-[10px] font-semibold bg-blue-500 text-white dark:bg-blue-600 flex items-center justify-center">
                      {statusCounts.data.pendingCount}
                    </span>
                  )}
                  {item.id === 'draft-quote' && (statusCounts?.data?.approvedCount ?? 0) > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[1rem] h-4 px-1 rounded-full text-[10px] font-semibold bg-blue-500 text-white dark:bg-blue-600 flex items-center justify-center">
                      {statusCounts.data.approvedCount}
                    </span>
                  )}
                  {item.id === 'quote-evaluation' && (statusCounts?.data?.readyToEvaluateCount ?? 0) > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[1rem] h-4 px-1 rounded-full text-[10px] font-semibold bg-blue-500 text-white dark:bg-blue-600 flex items-center justify-center">
                      {statusCounts.data.readyToEvaluateCount}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </nav>

        {/* Footer */}
        {!isIconOnlyMode && (
          <div className="p-2 border-t border-gray-200 dark:border-gray-700 space-y-2 flex-shrink-0">
            {/* Dark Mode Toggle */}
            <div className="flex items-center justify-between p-2 rounded-lg bg-gray-50 dark:bg-gray-800">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Dark Mode</span>
              <button
                onClick={toggleTheme}
                className="relative w-12 h-6 bg-gray-200 dark:bg-blue-600 rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
                aria-label="Toggle dark mode"
              >
                <div
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-md transform transition-transform duration-300 ${isDarkMode ? 'translate-x-6' : 'translate-x-0'
                    }`}
                >
                  <div className="flex items-center justify-center h-full">
                    {isDarkMode ? (
                      <i className="fas fa-moon text-blue-600 text-xs"></i>
                    ) : (
                      <i className="fas fa-sun text-yellow-400 text-xs"></i>
                    )}
                  </div>
                </div>
              </button>
            </div>

            {/* Profile Section */}
            <div className="relative">
              <button
                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                className="w-full flex items-center space-x-2 p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <div className="w-8 h-8 rounded-full border-2 border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                  <i className="fas fa-user text-gray-500 dark:text-gray-400 text-sm"></i>
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium">{user?.fullName}</p> {/* fullName */}
                  <p className="text-xs text-gray-500 dark:text-gray-400">Kullanıcı</p>
                </div>
                <i className="fas fa-chevron-up text-xs"></i>
              </button>

              {showProfileDropdown && (
                <div className="absolute bottom-full left-0 right-0 mb-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                  <div className="py-2">
                    <button
                      onClick={() => dispatch(logout())}
                      className="w-full text-left px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center space-x-2 transition-colors"
                    >
                      <i className="fas fa-sign-out-alt text-red-500"></i>
                      <span>Çıkış Yap</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Collapsed Footer - Only Dark Mode Toggle */}
        {isIconOnlyMode && (
          <div className="p-1 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
            <button
              onClick={toggleTheme}
              className="w-full p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              title="Toggle Dark Mode"
            >
              <i className={`fas ${isDarkMode ? 'fa-moon text-blue-600' : 'fa-sun text-yellow-400'} text-lg`}></i>
            </button>
          </div>
        )}

        {/* Resize Handle - Only on desktop */}
        {isOpen && !isMobile && (
          <div
            className="absolute top-0 right-0 w-1 h-full bg-transparent hover:bg-blue-400 dark:hover:bg-blue-500 cursor-col-resize transition-colors duration-200 group"
            onMouseDown={handleMouseDown}
            title="Sidebar genişliğini ayarlamak için sürükleyin"
          >
            <div className="w-1 h-full bg-gray-300 dark:bg-gray-600 group-hover:bg-blue-400 dark:group-hover:bg-blue-500 transition-colors duration-200"></div>
          </div>
        )}
      </div>
    </>
  );
};

export default Sidebar;
